import React from 'react';
import { ChartTooltip } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * Shape of the props Recharts injects when this component is used as a
 * `<Tooltip content>` element. `payload[i].payload.fill` is populated by
 * recharts from the matching `<PieChart.Cell />`, so the indicator can
 * pick up the exact wedge colour without us having to thread it through
 * separately.
 */
export type PopularBrandsTooltipProps = {
    active?: boolean;
    payload?: ReadonlyArray<{
        name?: string;
        value?: number | string;
        payload?: { fill?: string };
    }>;
    valueFormatter?: (value: number | string) => React.ReactNode;
};

/**
 * Brand-coloured chart tooltip for the pie. Returns `null` when inactive
 * or the payload is empty so recharts can keep the tooltip layer hidden
 * without an empty bubble flashing on screen.
 */
export const PopularBrandsTooltip: React.FC<PopularBrandsTooltipProps> = ({
    active,
    payload,
    valueFormatter,
}) => {
    if (!active || !payload?.length) {
        return null;
    }

    return (
        <ChartTooltip {...getNodeTestIdProp('popular-brands-tooltip')}>
            {payload.map((entry, index) => (
                <ChartTooltip.Item
                    key={`${entry.name ?? ''}-${index}`}
                    {...getNodeTestIdProp(
                        `popular-brands-tooltip-item-${index}`,
                    )}
                >
                    <ChartTooltip.Indicator
                        color={entry.payload?.fill}
                        {...getNodeTestIdProp(
                            `popular-brands-tooltip-indicator-${index}`,
                        )}
                    />
                    <ChartTooltip.Label
                        {...getNodeTestIdProp(
                            `popular-brands-tooltip-label-${index}`,
                        )}
                    >
                        {entry.name}
                    </ChartTooltip.Label>
                    <ChartTooltip.Value
                        {...getNodeTestIdProp(
                            `popular-brands-tooltip-value-${index}`,
                        )}
                    >
                        {valueFormatter
                            ? valueFormatter(entry.value ?? '')
                            : entry.value}
                    </ChartTooltip.Value>
                </ChartTooltip.Item>
            ))}
        </ChartTooltip>
    );
};
